<template>
  <header>
    <div class="relative flex min-h-[65vh] flex-col">
      <!-- IMAGE OR VIDEO -->
      <div class="absolute inset-0 -z-10 overflow-hidden">
        <AppImage
          v-if="imageOrVideo.image"
          :src="imageOrVideo.image"
          :alt="
            $i18n.locale === 'fr'
              ? imageOrVideo.image.alt_fr
              : imageOrVideo.image.alt
          "
          class="size-full object-cover"
          :style="`object-position: ${imageOrVideo.image.focus_css};`"
        />

        <AppImage
          v-else-if="
            imageOrVideo.video &&
            data.hero_only_image &&
            imageOrVideo.cover_image
          "
          :alt="
            $i18n.locale === 'fr'
              ? imageOrVideo.cover_image.alt_fr
              : imageOrVideo.cover_image.alt
          "
          :src="imageOrVideo.cover_image"
          class="size-full object-cover"
          :style="`object-position: ${imageOrVideo.cover_image.focus_css};`"
        />

        <VideoPlayer
          v-else-if="imageOrVideo.video"
          :src="imageOrVideo.video"
          :autoplay="true"
          class="size-full scale-[1.2] transition-transform"
        />
        <div
          v-else
          class="size-full bg-gradient-to-tr from-blue-dark-800 to-blue-dark-600"
        />
      </div>

      <HeroImageGradient />

      <div
        class="z-10 flex w-full grow flex-col justify-end pb-12 pt-navigation-desktop text-white"
      >
        <div
          id="elTitle"
          class="grid-cols-4 xl:grid xl:pr-container-desktop-wide"
        >
          <Container indent="wide" class="col-span-3">
            <!-- BACK LINK -->
            <button
              class="group -m-1 my-5 flex items-center gap-3 p-1 text-base ring-white ring-offset-0 sm:my-7"
              @click="$router.back()"
            >
              <ArrowGrowIcon class="-ml-2 w-6 rotate-180" />
              <span class="first-letter:uppercase">
                {{ $t('back') }}
              </span>
            </button>

            <AnimatedContainer>
              <!-- TITLE -->
              <h1
                v-if="data.hero_title"
                class="fade-in mb-4 max-w-title hyphens-auto text-3xl font-medium leading-9 sm:mb-6 sm:text-6xl"
                v-html="$replaceHtmlTags(data.hero_title)"
              />

              <time
                :datetime="machineReadableDateTime"
                class="fade-in mb-7 inline-block text-white/60 first-letter:uppercase sm:mb-12"
              >
                {{ dateTime }}
              </time>
            </AnimatedContainer>

            <!-- ACTIONS -->
            <TypeTag layout="medium">
              {{ data.article_type.title }}
            </TypeTag>

            <div
              v-if="data.file_pdf || data.news_article"
              class="mt-4 flex flex-wrap gap-x-6 gap-y-4"
            >
              <!-- Pdf download -->
              <AppButtonFileDownload is-blue :file="data.file_pdf">
                <template #default>
                  {{ $t('download_pdf') }}
                </template>
              </AppButtonFileDownload>

              <!-- News Article Link -->
              <AppButton
                v-if="data.news_article"
                is-secondary
                :to="data.news_article.url"
              >
                {{ $t('read_full_article') }}
              </AppButton>
            </div>
          </Container>
        </div>
      </div>
    </div>

    <!-- MOBILE -->
    <slot name="sidebar" />

    <!-- LEAD -->
    <div id="elLead">
      <Container indent="wide">
        <div
          v-if="data.lead"
          class="max-w-text pt-16 text-md font-medium"
          v-html="data.lead"
        />
      </Container>
    </div>
  </header>
</template>

<script setup>
const { t, locale } = useI18n()

const props = defineProps({
  data: { type: Object, required: true },
})

const imageOrVideo = computed(() => {
  return props.data?.image_or_video?.[0] || false
})

const dateTime = computed(() => {
  if (!props.data?.date) {
    return null
  }

  let date = new Date(props.data.date).toLocaleDateString(
    locale.value || 'de',
    {
      month: 'long',
      year: 'numeric',
    }
  )

  if (props.data.time) {
    date += `, ${props.data.time} ${t('hrs')}`
  }

  return date
})

const machineReadableDateTime = computed(() => {
  if (!props.data?.date) {
    return null
  }

  if (!props.data.time) {
    return props.data.date
  }

  // Create a Date object using the date string
  const date = new Date(props.data.date)

  // Extract the hour and minute from the time string
  const [hour, minute] = props.data.time.split(':').map(Number)

  // Set the hour and minute to the Date object
  date.setHours(hour, minute)

  return date
})

const emit = defineEmits(['resize'])
const textHeight = ref({})
const debouncedOnResize = useDebounceFn(onResize, 500)

// FUNCTIONS
function onResize() {
  if (window.innerWidth < 1280) {
    return
  }

  // the ref approach doesn't work initial after navigating to the page with nuxtLink (returns 0 as height except you wait for > 500ms)
  textHeight.value = {
    title: document.getElementById('elTitle')?.offsetHeight,
    lead: document.getElementById('elLead')?.offsetHeight,
  }
}

// WATCHERS
watch(
  textHeight,
  (newValue) => {
    emit('resize', newValue.title + newValue.lead)
  },
  { deep: true }
)

// MOUNTED
onMounted(async () => {
  window.addEventListener('resize', debouncedOnResize)

  // Wait for page animation to finish
  await new Promise((resolve) => setTimeout(resolve, 300))

  onResize()
})

onUnmounted(() => {
  window.removeEventListener('resize', debouncedOnResize)
})
</script>
